import { useEffect, useRef, useState } from 'react'
import { Button } from '@/app/common/components/button/Button.tsx'
import { TableToolBar } from '@/common/components/StackedTable.tsx'
import { cn, getSemanticColor } from '@/common/utilities.ts'
import Pagination from '@/common/components/Pagination.tsx'
import {
  Card,
  CardContent,
  CardHeader,
  CardFooter,
} from '@/common/components/Card.tsx'
import { ActionBar } from '@/layouts/ActionBar.tsx'
import Badge from '@/common/components/Badge.tsx'
import SearchInput from '@/common/components/SearchInput.tsx'
import SimpleSelector from '@/common/components/SimpleSelector.tsx'
import { EmailIcon, FilterIcon } from '@/common/icons/general.tsx'
import AppDialog from '@/common/components/Dialog.tsx'
import { Input } from '@/app/common/components/input/Input.tsx'
import RoleControl from './components/RoleControl.tsx'
import { useAppAlert } from '@/layouts/AppAlertProvider.tsx'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { SubmitHandler, useForm } from 'react-hook-form'
import FormErrorMessage from '@/common/components/FormErrorMessage.tsx'
import {
  inviteUser,
  LogUserActivity,
  ResendEmailInvitation,
  SendResetPasswordEmail,
} from '@/apis/users/requests.ts'
import { RoleQueries } from '@/queries/roles/queries.ts'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { EllipsesLoader } from '@/common/components/EllipsesLoader.tsx'
import { useNavigate } from 'react-router-dom'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { Role } from '@/common/types_and_interfaces.ts'
import { UserListQueries } from '@/queries/users/queries.ts'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import {
  UserList,
  SchemaUserListList,
  getUserListStatusText,
  UpdateUserData,
  RolesListSchema,
  getExportedData,
} from '@/queries/users/requests.ts'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from '@/common/components/Menu.tsx'
import {
  CalendarIcon,
  CloseIcon,
  EditIconGeneral,
  KebabMenuIcon,
  UserCheckIcon,
} from '@/common/icons/general.tsx'
import UserSelect, { User } from './components/UserSelect.tsx'
import { useSelector } from 'react-redux'
import { RootState } from '@/storage/store.ts'
import RolesControl from '@/pages/setup/users/components/RolesControl.tsx'
import axios from 'axios'
import { useUserPrivileges } from '../../../../hook/useUserPrivileges.tsx'
import usePermissionAlert from '../../../../hook/usePermissionAlert.tsx'
import { LOG_TYPES } from '@/common/constants.ts'

const UserInviteSchema = z.object({
  email: z.string().email({ message: 'Please enter a valid email address' }),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  selectedUserId: z.number().optional().nullable(),
})
type UserInviteFormProps = z.infer<typeof UserInviteSchema>

const TABLE_FILTERS = [
  { id: 8888, name: 'All' },
  { id: 1, name: 'Active' },
  { id: 0, name: 'Inactive' },
  { id: 3, name: 'Pending' },
]
const RECORDS_PER_PAGE = 10 as const
const THREE_MINUTES = 1000 * 60 * 3
const InitialUserList: SchemaUserListList = [] as const
const UsersView = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [selectedRole, setSelectedRole] = useState<Role>()
  //const startIndex = (currentPage - 1) * RECORDS_PER_PAGE
  //const selectedData = people.slice(startIndex, startIndex + RECORDS_PER_PAGE)
  const [open, setOpen] = useState(false)
  const [openEditRolesModal, setOpenEditRolesModal] = useState(false)
  const [editRoleUserId, setEditRoleUserId] = useState<number | null>(null)
  type UserRole = z.infer<typeof RolesListSchema>
  const [selectedRoles, setSelectedRoles] = useState<UserRole[]>([])
  const [selectedFilter, setSelectedFilter] = useState(TABLE_FILTERS[0])
  const { selectedProgram, programYears } = useSelector(
    (s: RootState) => s.programs
  )
  const [roles, setRoles] = useState<Role[]>([])
  const { fireAlert } = useAppAlert()
  const [search, setsearch] = useState('')
  const roleQueryResult = useQuery(
    RoleQueries.index(selectedProgram?.id, { enabled: !!selectedProgram })
  )
  const [recordsPerPage, setRecordsPerPage] = useState<number>(RECORDS_PER_PAGE)

  const navigate = useNavigate()

  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const handleEmailSelection = (selectedUsers: User | null) => {
    setSelectedUser(selectedUsers)
    setValue('email', selectedUsers?.email ?? '')
    trigger('email')
    setValue('selectedUserId', selectedUsers?.id ?? null)
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    trigger,
  } = useForm<UserInviteFormProps>({
    resolver: zodResolver(UserInviteSchema),
  })

  const { showPermissionDeniedMessage } = usePermissionAlert()
  const privileges = useUserPrivileges()

  const hasLoggedPageView = useRef(false)
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'Users',
      }) // Log page view
      hasLoggedPageView.current = true // Mark as executed
    }
  }, [])

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  const handlePerPageChange = (perPage: number) => {
    setRecordsPerPage(perPage)
  }

  const handleResetPassword = async (email: string | null) => {
    try {
      if (email) {
        const response = await SendResetPasswordEmail(email)
        const { error } = response
        if (!error) {
          await fireAlert({
            variant: 'success',
            message: response.data.message,
          })
        }
      }
    } catch (e) {
      console.error(e)
    }
  }

  const handleResendInvitationEmail = async (email: string | null) => {
    if (email) {
      const response = await ResendEmailInvitation(
        email,
        `https://${window.location.hostname}/sign_up`
      )
      const { error } = response
      if (!error) {
        await fireAlert({
          variant: 'success',
          message: response.data.message,
        })
      }
    }
  }

  useEffect(() => {
    if (!selectedProgram) {
      navigate(AppRoutePaths.programs.index)
    }
  }, [])

  useEffect(() => {
    if (roleQueryResult?.data?.data && roles.length == 0) {
      setRoles(
        roleQueryResult.data.data.filter((f) => f.code != 'family_applicant')
      )
    }
  }, [roleQueryResult.data])

  useEffect(() => {
    if (open) {
      reset()
    }
  }, [open])

  const handleDeactivate = (user_id: number, status: number) => {
    fireAlert({
      variant: 'warning',
      confirmBtnText: 'Yes',
      denyBtnText: 'No',
      denyBtn: true,
      message:
        status != 2
          ? 'Are you sure you want to Deactivate?'
          : 'Are you sure you want to Reactivate?',
    }).then(async ({ confirmed }) => {
      if (confirmed) {
        await UpdateUserData(
          {
            status: status ? 0 : 1,
          },
          user_id
        )
        UserListQuery.refetch()
      }
    })
  }

  const handleEditUser = (user_id: number) => {
    navigate(AppRoutePaths.setup.users.details.replace(':userId', `${user_id}`))
  }

  const handleUserHistory = (user_id: number) => {
    navigate(AppRoutePaths.setup.users.history.replace(':userId', `${user_id}`))
  }

  const onSubmit: SubmitHandler<UserInviteFormProps> = async (formData) => {
    if (!selectedRole) {
      fireAlert({ variant: 'error', message: 'Please select a role' })
      return
    }
    setLoading(true)
    const { error } = await inviteUser({
      first_name: formData.firstName,
      last_name: formData.lastName,
      phone: '',
      selectedUserId: formData.selectedUserId,
      email: selectedUser?.email ?? '',
      role_id: [selectedRole.id],
      program_id: selectedProgram?.id,
      invite_callback_url: `https://${window.location.hostname}/sign_up`,
    })
    setLoading(false)
    if (error) {
      await fireAlert({
        variant: 'error',
        message: error.message,
      })
      return
    } else {
      fireAlert({ variant: 'success', message: 'User invited successfully' })
      setOpen(false)
      reset()
    }
  }

  const onSubmitUpdateRoles = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (selectedRoles.length < 1) {
      fireAlert({ variant: 'error', message: 'Please select at least a role' })
      return
    }

    const rolesIds = selectedRoles.map((role) => role.id)
    const payload = {
      roles: rolesIds, // Replace with dynamic values if needed
      global_program_id: programYears[0]?.global_program_id,
      // program_id: selectedProgram?.id,
    }

    try {
      const url = `users/${editRoleUserId}/roles`
      await axios.put(url, payload)
      UserListQuery.refetch()
      fireAlert({ variant: 'success', message: 'Roles updated successfully' })
      setOpenEditRolesModal(false)
    } catch (error) {
      fireAlert({
        variant: 'warning',
        message: 'Error updating the role.',
      })
    }
  }

  function getFullName(
    first_name?: string | null,
    last_name?: string | null
  ): string {
    return `${first_name ?? ''} ${last_name ?? ''}`
  }

  function formatRoles(roles?: string | null): string {
    return roles?.split('_').join(' ') ?? ''
  }

  // Loading Table
  const columnHelper = createColumnHelper<UserList>()
  const columns = [
    columnHelper.accessor((row) => getFullName(row.first_name, row.last_name), {
      id: 'fullName',
      header: () => (
        <>
          <span className="block pl-4 2xl:hidden">User</span>
          <span className="hidden pl-4 2xl:block">Name</span>
        </>
      ),
      cell: (info) => {
        const row = info.row.original
        return (
          <div className="flex items-center">
            <div className="w-3/4 sm:w-full font-bold pl-4">
              <div className="font-medium text-gray-900 flex justify-between items-center">
                <span> {getFullName(row.first_name, row.last_name)} </span>
                <Badge
                  className="md:hidden"
                  semanticColor={getSemanticColor(
                    getUserListStatusText(row.status)
                  )}
                >
                  {getUserListStatusText(row.status)}
                </Badge>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div>{getFullName(row.email)}</div>
              </div>
              <div className="mt-2 space-y-1 text-gray-500 font-normal md:hidden">
                <div className="capitalize">{formatRoles(row.roles)}</div>
              </div>
            </div>
          </div>
        )
      },
    }),
    columnHelper.accessor('email', {
      header: () => <span>Email</span>,
      cell: (info) => <span>{info.getValue()}</span>,
    }),
    columnHelper.accessor('status', {
      header: () => <span>Status</span>,
      cell: (info) => (
        <div>
          <Badge
            semanticColor={getSemanticColor(
              getUserListStatusText(info.getValue())
            )}
          >
            {getUserListStatusText(info.getValue())}
          </Badge>
        </div>
      ),
    }),
    columnHelper.accessor('roles', {
      header: () => <span>Roles</span>,
      cell: (info) => (
        <span className="capitalize">{formatRoles(info.getValue())}</span>
      ),
    }),
    columnHelper.display({
      id: 'action',
      header: () => <span className="pr-2 2xl:p-1">Action</span>,
      cell: (info) => {
        const row = info.row.original
        return (
          <Menu>
            <MenuButton>
              <KebabMenuIcon />
            </MenuButton>
            <MenuItems className="w-44 bottom-8 right-0">
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!privileges.user.users_edit) {
                    showPermissionDeniedMessage()
                    return
                  }
                  handleEditUser(row.id)
                }}
              >
                <EditIconGeneral className="text-lg text-gray-400 group-hover:text-primary" />
                <span>Edit</span>
              </MenuItem>
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!privileges.user.users_edit) {
                    showPermissionDeniedMessage()
                    return
                  }
                  setSelectedRoles(row.roles_list ?? [])
                  setEditRoleUserId(row.id)
                  setOpenEditRolesModal(true)
                }}
              >
                <EditIconGeneral className="text-lg text-gray-400 group-hover:text-primary" />
                <span>Edit Roles</span>
              </MenuItem>
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  handleUserHistory(row.id)
                }}
              >
                <CalendarIcon className="text-lg text-gray-400 group-hover:text-primary" />
                <span>History</span>
              </MenuItem>
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!privileges.user.users_edit) {
                    showPermissionDeniedMessage()
                    return
                  }
                  row.status === 3
                    ? handleResendInvitationEmail(row?.email)
                    : handleResetPassword(row?.email)
                }}
              >
                <EmailIcon className="text-lg text-gray-400 group-hover:text-primary" />
                <span>
                  {row.status == 3 ? 'Resend Invite' : 'Forgot Password'}
                </span>
              </MenuItem>
              <MenuItem
                className="group flex justify-start items-center gap-2"
                onClick={() => {
                  if (!privileges.user.users_delete) {
                    showPermissionDeniedMessage()
                    return
                  }
                  handleDeactivate(row.id, row.status)
                }}
              >
                {row.status != 2 && (
                  <CloseIcon className="text-lg text-gray-400 group-hover:text-primary" />
                )}
                {row.status === 2 && (
                  <UserCheckIcon className="text-lg text-gray-400 group-hover:text-primary" />
                )}
                <span>{row.status ? 'Deactivate' : 'Reactivate'}</span>
              </MenuItem>
            </MenuItems>
          </Menu>
        )
      },
    }),
  ]
  const UserListQuery = useQuery(
    UserListQueries.getPaginatedListByProgramId(
      recordsPerPage,
      currentPage,
      selectedProgram?.id,
      search,
      {
        enabled: !!selectedProgram,
        placeholderData: keepPreviousData,
        refetchInterval: THREE_MINUTES,
      },
      selectedFilter.id === 8888 ? null : selectedFilter.id,
      roles.map((r) => r.code)
    )
  )

  const table = useReactTable({
    data: UserListQuery.data?.data ?? InitialUserList,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: UserListQuery.data?.last_page,
  })

  const exportToCSV = async () => {
    if (!UserListQuery.data?.data || UserListQuery.data.data.length === 0) {
      return fireAlert({
        variant: 'warning',
        message: 'No data available to export.',
      })
    }

    const users = await getExportedData(
      selectedFilter.id === 8888 ? null : selectedFilter.id,
      search,
      selectedProgram?.id,
      roles.map((r) => r.code)
    )

    const url = window.URL.createObjectURL(new Blob([users.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', 'Users.csv') // Nombre del archivo
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const handleRoleSelection = (role: Role, checked: boolean) => {
    setSelectedRoles(
      (prevRoles) =>
        checked
          ? [...prevRoles, role] // Add the role if checked
          : prevRoles.filter((r) => r.id !== role.id) // Remove based on `id`
    )
  }

  return (
    <>
      <ActionBar>
        {privileges.user.users_create && (
          <Button
            size="lg"
            className="font-bold"
            onClick={() => setOpen(!open)}
          >
            Invite User
          </Button>
        )}
      </ActionBar>
      <Card className="mt-4">
        <CardHeader className="flex justify-between items-center text-md font-semibold">
          <div className="text-left text-md font-semibold text-gray-900">
            Users
          </div>
          <TableToolBar>
            <SearchInput
              id="table_search"
              placeholder="Search Users"
              value={search}
              onChange={(e) => setsearch(e.target.value)}
            />
            <SimpleSelector
              list={TABLE_FILTERS}
              selected={selectedFilter}
              setSelected={setSelectedFilter}
              className="pt-4 lg-sm:ps-4 lg-sm:pt-0"
              icon={<FilterIcon className="text-primary" />}
            />
            <Button size="lg" className="font-bold mx-5" onClick={exportToCSV}>
              Export
            </Button>
          </TableToolBar>
        </CardHeader>
        <CardContent className="flex flex-col p-0 lg:p-0 h-full w-full">
          <table>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => (
                    <th
                      key={header.id}
                      className={cn(
                        'w-[15%] px-2 py-3.5 text-left text-sm font-semibold text-gray-900 md:py-4 md:px-2.5',
                        (header.id === 'fullName' || header.id === 'email') &&
                          'w-[28%]',
                        index != 0 &&
                          index != headerGroup.headers.length - 1 &&
                          'hidden md:table-cell',
                        index == headerGroup.headers.length - 1 && 'text-center'
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            {(function () {
              if (UserListQuery.error) {
                return (
                  <tbody>
                    <tr>
                      <td colSpan={5} className="text-center p-2">
                        {!Array.isArray(UserListQuery?.data) ||
                        UserListQuery.data.length === 0
                          ? 'No users found. Please invite new user'
                          : null}
                      </td>
                    </tr>
                  </tbody>
                )
              } else {
                return (
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr key={row.id}>
                        {row.getVisibleCells().map((cell, index) => (
                          <td
                            key={cell.id}
                            className={cn(
                              'w-[15%] py-4 px-2 text-sm font-sans text-left text-gray-500 border-t border-gray-200 md:py-4 md:px-2 max-w-40 text-ellipsis',
                              (cell.id.includes('fullName') ||
                                cell.id.includes('email')) &&
                                'w-[28%]',
                              index != 0 &&
                                index != row.getVisibleCells().length - 1 &&
                                'hidden md:table-cell',
                              index == row.getVisibleCells().length - 1 &&
                                'text-center'
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                )
              }
            })()}
          </table>
        </CardContent>
        <CardFooter className="p-0">
          <Pagination
            itemsPerPage={recordsPerPage}
            currentPage={currentPage}
            totalRows={UserListQuery.data?.total ?? 0}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </CardFooter>
      </Card>

      <AppDialog open={open} setOpen={setOpen}>
        <AppDialog.Title>Invite User</AppDialog.Title>
        <form
          noValidate
          className="space-y-3"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="flex flex-col mt-6 mx-2 space-y-3">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-bold leading-6 text-gray-900"
              >
                Program<span className="text-danger ml-1">*</span>
              </label>
              <div className="mt-2">
                <Input value={selectedProgram?.name ?? ''} disabled />
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-bold leading-6 text-gray-900 mb-2"
              >
                Email<span className="text-danger ml-1">*</span>
              </label>
              {/* <div className="mt-2">
                                <Input
                                    {...register('email')}
                                    type="text"
                                    required
                                    autoComplete="email"
                                    placeholder="Enter Email"
                                />

                            </div> */}
              <UserSelect onSelect={handleEmailSelection} />
              <FormErrorMessage message={errors.email?.message} />
            </div>
            <div>
              <label
                htmlFor="firstName"
                className="block text-sm font-bold leading-6 text-gray-900 mb-2"
              >
                First Name<span className="text-danger ml-1">*</span>
              </label>
              <div className="mt-1">
                <Input
                  {...register('firstName')}
                  type="text"
                  required
                  autoComplete="firstName"
                  placeholder="Enter First Name"
                />
                <FormErrorMessage message={errors.firstName?.message} />
              </div>
            </div>

            <div>
              <label
                htmlFor="lastName"
                className="block text-sm font-bold leading-6 text-gray-900 mb-2"
              >
                Last Name<span className="text-danger ml-1">*</span>
              </label>
              <div className="mt-1">
                <Input
                  {...register('lastName')}
                  type="text"
                  required
                  autoComplete="lastName"
                  placeholder="Enter Last Name"
                />
                <FormErrorMessage message={errors.lastName?.message} />
              </div>
            </div>

            <div className="mt-15">
              <span className="block text-sm font-bold leading-6 text-gray-900 mb-2">
                Role<span className="text-danger ml-1">*</span>
              </span>
              <div className="grid gap-2">
                {roles.map((role: Role, index: number) => (
                  <>
                    {role.code !== 'admin' && (
                      <RoleControl
                        key={index}
                        title={role.name}
                        description={role.description}
                        onCheckedChange={() => {
                          setSelectedRole(role)
                        }}
                      />
                    )}
                  </>
                ))}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-x-2">
              {loading ? (
                <EllipsesLoader size="md" />
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpen(false)
                      reset()
                    }}
                    type="reset"
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Submit</Button>
                </>
              )}
            </div>
          </div>
        </form>
      </AppDialog>

      <AppDialog open={openEditRolesModal} setOpen={setOpenEditRolesModal}>
        <AppDialog.Title>Select Roles</AppDialog.Title>
        <form noValidate className="space-y-3" onSubmit={onSubmitUpdateRoles}>
          <div className="flex flex-col mt-6 mx-2 space-y-3">
            <div className="mt-15">
              <span className="block text-sm font-bold leading-6 text-gray-900 mb-2">
                Roles<span className="text-danger ml-1">*</span>
              </span>
              <div className="grid gap-2">
                {roles.map((role) =>
                  role.code !== 'admin' ? (
                    <RolesControl
                      key={role.code}
                      title={role.name}
                      description={role.description}
                      isChecked={selectedRoles.some(
                        (r) => r.code === role.code
                      )}
                      onCheckedChange={(checked) =>
                        handleRoleSelection(role, checked)
                      }
                    />
                  ) : null
                )}
              </div>
            </div>

            <div className="mt-6 flex items-center justify-end gap-x-2">
              {loading ? (
                <EllipsesLoader size="md" />
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpenEditRolesModal(false)
                      reset()
                    }}
                    type="button"
                  >
                    Cancel
                  </Button>
                  <Button type="submit">Submit</Button>
                </>
              )}
            </div>
          </div>
        </form>
      </AppDialog>
    </>
  )
}

export default UsersView
